import { useEffect, useRef } from 'react';

interface Options {
  minLength?: number;
  maxIntervalMs?: number;
  /**
   * Stop listening while some other surface owns the keyboard.
   *
   * This exists because the wedge listens on `window` and fires even when a form field has
   * focus — deliberately, since a focus-stealing scanner feeds the focused input anyway. The
   * case that breaks is a dialog asking the operator to TYPE a code: the keystrokes reach the
   * field AND the buffer, and the Enter that submits the dialog also looks like the end of a
   * scan, so one typed EAN counts twice. `window.prompt` was immune only because it froze the
   * page; a real dialog has to say so.
   */
  paused?: boolean;
  onScan: (text: string) => void;
}

/**
 * Part F global behaviour — Bluetooth HID scanner in keyboard-wedge mode.
 *
 * The scanner emits all characters within a few milliseconds. We accumulate
 * key events and call onScan only if ≥ minLength chars arrived in
 * ≤ maxIntervalMs total, then the Enter / break came.
 *
 * Defaults match the spec: > 6 chars in < 100 ms = scan, not typing.
 */
export function useScanInput({
  minLength = 6,
  maxIntervalMs = 100,
  paused = false,
  onScan,
}: Options) {
  const buf = useRef<string>('');
  const lastTs = useRef<number>(0);

  useEffect(() => {
    // Drop whatever was half-typed: on resuming, a burst must start clean rather than glue
    // itself to the characters that went into the dialog.
    if (paused) {
      buf.current = '';
      return;
    }
    const handler = (e: KeyboardEvent) => {
      // Skip when the user is typing into an input/textarea — scanner reports as
      // if focused on body. Real focus-stealing scanners feed inputs anyway.
      const target = e.target as HTMLElement | null;
      const isFormField =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

      const now = performance.now();
      if (e.key === 'Enter') {
        const text = buf.current;
        buf.current = '';
        if (text.length >= minLength && now - lastTs.current <= maxIntervalMs * text.length) {
          if (!isFormField) e.preventDefault();
          onScan(text);
        }
        return;
      }
      // Single printable character
      if (e.key.length === 1) {
        if (now - lastTs.current > 500) buf.current = ''; // new burst
        buf.current += e.key;
        lastTs.current = now;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [minLength, maxIntervalMs, paused, onScan]);
}
